import { ENTER, COMMA } from '@angular/cdk/keycodes';
import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
} from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, debounceTime, switchMap, map, of, fromEvent } from 'rxjs';
import { CanvasService } from '../canvas.service';
import { cloneDeep } from 'lodash';
import { ShareAccessLevel, SharedUser } from '../canvas.model';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { CanvasHelperService } from '../canvas-helper.service';

@Component({
  selector: 'app-section-autocomplete',
  templateUrl: './section-autocomplete.component.html',
  styleUrl: './section-autocomplete.component.scss',
})
export class SectionAutocompleteComponent {
  readonly shareAccess = ShareAccessLevel;

  @Input() selectedUsersList: SharedUser[] = [];
  @Input() loggedUserId: string = '';
  @Output() onUserSelectedFromDropdown = new EventEmitter<any>();
  separatorKeysCodes: number[] = [ENTER, COMMA];
  allUsersList: SharedUser[] = [];
  userInput: ElementRef | undefined;
  isLoadingUsers: boolean = false;

  userCtrl = new FormControl<string>('');

  @ViewChild('userInput') set setUserInput(ref: ElementRef | undefined) {
    if (ref) {
      this.userInput = ref;
    }
  }

  constructor(
    private readonly canvasService: CanvasService,
    private readonly canvasHelperService: CanvasHelperService
  ) {}

  private setupEditorListener() {
    fromEvent(this.userInput?.nativeElement, 'input')
      .pipe(
        startWith<any>(''),
        debounceTime(500),
        map(() => this.userInput?.nativeElement.value?.trim() ?? ''),
        distinctUntilChanged(),
        switchMap((query: string) => {
          if (!query || query.length < 3) {
            this.isLoadingUsers = false;
            return of([]);
          }
          this.isLoadingUsers = true;
          return this.fetchUsers(query);
        })
      )
      .subscribe((users) => {
        this.allUsersList =
          users?.filter(
            (user: SharedUser) => user.user_id !== this.loggedUserId
          ) ?? [];
        this.isLoadingUsers = false;
      });
  }

  ngAfterViewInit(): void {
    this.setupEditorListener();
  }

  fetchUsers(query: string): Observable<SharedUser[]> {
    return this.canvasService.getUsersList(query).pipe(
      map((users: any) => {
        if (users?.length > 0) {
          users = users
            .map((user: any) => {
              return {
                user_id: user.UserId,
                user_email: user.Email,
                permission: this.shareAccess.READ_ONLY,
              };
            })
            ?.sort((a: SharedUser, b: SharedUser) =>
              a.user_email.localeCompare(b.user_email)
            );
        }
        return users;
      })
    );
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const selectedUser = event.option.value as SharedUser;
    const sharedCount = this.selectedUsersList.length + 1;

    if (
      sharedCount >
      this.canvasService.conversationSettings.session_share_max_limit
    ) {
      this.canvasHelperService.checkIfSharingAllowed(
        sharedCount,
        this.canvasService.conversationSettings
      );
      this.clearInput();
    } else {
      if (!this.isSelected(selectedUser)) {
        this.selectedUsersList.push({
          ...selectedUser,
          temporary_user: true,
          permission: this.shareAccess.READ_ONLY,
        });
      }
      this.clearInput();
      this.onUserSelectedFromDropdown.emit(cloneDeep(this.selectedUsersList));
    }
  }

  isSelected(user: SharedUser): boolean {
    return this.selectedUsersList.some((u) => u.user_id === user.user_id);
  }

  clearInput(serachQuery: string = ''): void {
    if (this.userInput?.nativeElement) {
      this.userInput.nativeElement.value = serachQuery;
    }
  }

  displayFn(user: SharedUser): string {
    return user?.user_email || '';
  }

  showNoUsersFound(): boolean {
    return (
      typeof this.userInput?.nativeElement?.value === 'string' &&
      this.userInput?.nativeElement?.value.length >= 3 &&
      this.allUsersList.length === 0
    );
  }

  checkOpptionSelected(sec: SharedUser) {
    return (
      this.selectedUsersList.findIndex(
        (it: SharedUser) => it.user_id == sec.user_id
      ) != -1
    );
  }
}
